import React, { useState, useContext, useEffect } from 'react';
import SectionHeading from '../Typography/SectionHeading/SectionHeading';
import EditButton from '../Typography/EditButton/EditButton';
import styles from './FONASUContribCustomerInfo.css';
import { FlowContext } from '../../core/flow-engine';

import { InternalAuthenticatorContext } from '../../context/InternalAuthenticatorContext/InternalAuthenticatorContext';
import FONASUCustomerReview from '../FONASUCustomerReview/FONASUCustomerReview';
import FONASUCustomerInformationSection from '../FONASUCustomerInformationSection/FONASUCustomerInformationSection';
import FONASUContriInvestmentAmount from '../FONASUContribInvestmentAmount/FONASUContribInvestmentAmount';
import FONASUContribInvestmentAmountReview from '../FONASUContribInvestmentAmountReview/FONASUContribInvestmentAmountReview';
import { extractPhoneNumberwithType } from '../../flows/shared/utils';

const CustomerContribReview = ({
  customer,
  phone1,
  phone2,
  phone3,
  currentYearAmount,
  priorYearAmount,
  totalInvestmentAmt,
  contributionLimits
}) => {
  const {
    appState: {
      store: { mailingAddressDifferent }
    }
  } = useContext(FlowContext);

  return (
    <>
      <FONASUCustomerReview
        customer={customer}
        phone1={phone1}
        phone2={phone2}
        phone3={phone3}
        mailingAddressDifferent={mailingAddressDifferent}
      />
      <FONASUContribInvestmentAmountReview
        currentYearAmount={currentYearAmount}
        priorYearAmount={priorYearAmount}
        totalInvestmentAmt={totalInvestmentAmt}
        contributionLimits={contributionLimits}
      />
    </>
  );
};

const shouldDisableDoneBtn = (
  meetsMinInvestmentAmt,
  exceedsContribAmt,
  showSuccess,
  phoneNumberRequired
) => {
  return (
    meetsMinInvestmentAmt ||
    exceedsContribAmt ||
    showSuccess ||
    phoneNumberRequired
  );
};

const FONASUContribCustomerInfo = ({
  showSuccess,
  editCustomer,
  setEditCustomer
}) => {
  const {
    next,
    formProps,
    appState: {
      store: {
        mailingAddressDifferent,
        app: { customer },
        loadedData: {
          contributionLimits,
          priorYearAmount,
          currentYearAmount,
          customerInfoEditMode
        }
      }
    }
  } = useContext(FlowContext);

  const {
    currentYearRemainingContributions,
    priorYearRemainingContributions
  } = contributionLimits;
  const { phones } = customer;
  const associateID = useContext(InternalAuthenticatorContext);

  const phone1 = extractPhoneNumberwithType(phones, 'MOBILE');
  const phone2 = extractPhoneNumberwithType(phones, 'DAYTIME');
  const phone3 = extractPhoneNumberwithType(phones, 'EVENING');

  const [priorYearInvAmount, setPriorYearInvAmount] = useState(priorYearAmount);
  const [curYearInvAmount, setCurYearInvAmount] = useState(currentYearAmount);
  const [totalInvestmentAmt, setTotalInvestmentAmt] = useState(
    priorYearAmount + currentYearAmount
  );

  const [meetsMinInvestmentAmt, setMeetsMinInvestmentAmt] = useState(true);
  const [exceedsContribAmt, setExceedsContribAmt] = useState(false);
  const [phoneNumberRequired, setPhoneNumberRequired] = useState(true);

  useEffect(() => {
    setEditCustomer(customerInfoEditMode);
  }, [customerInfoEditMode]);

  useEffect(() => {
    if (totalInvestmentAmt >= 1000) setMeetsMinInvestmentAmt(false);
    if (phone1 || phone2 || phone3) {
      setPhoneNumberRequired(false);
    }
  }, []);

  const disableDoneBtn = shouldDisableDoneBtn(
    meetsMinInvestmentAmt,
    exceedsContribAmt,
    showSuccess,
    phoneNumberRequired
  );

  const handleClick = async () => {
    if (editCustomer) {
      await next({
        section: 'addressValidation',
        associateLoginID: associateID
      })();
    } else {
      await next('unlockMyInfoSection')();
    }
  };

  const handleCurrentYearAmt = ({ target: { value } }) => {
    const currentYearAmtVal = Number(value);
    setCurYearInvAmount(currentYearAmtVal);

    const currentYearTotal = currentYearAmtVal + priorYearInvAmount;
    if (currentYearTotal >= 1000) {
      setMeetsMinInvestmentAmt(false);
    } else {
      setMeetsMinInvestmentAmt(true);
    }
    if (
      value > currentYearRemainingContributions ||
      priorYearInvAmount > priorYearRemainingContributions
    ) {
      setExceedsContribAmt(true);
    } else {
      setExceedsContribAmt(false);
    }
    setTotalInvestmentAmt(currentYearTotal);
  };

  const handlePriorYearAmt = ({ target: { value } }) => {
    const priorYearAmtVal = Number(value);
    setPriorYearInvAmount(priorYearAmtVal);

    const priorYearTotal = priorYearAmtVal + curYearInvAmount;
    if (priorYearTotal >= 1000) {
      setMeetsMinInvestmentAmt(false);
    } else {
      setMeetsMinInvestmentAmt(true);
    }
    if (
      value > priorYearRemainingContributions ||
      curYearInvAmount > currentYearRemainingContributions
    ) {
      setExceedsContribAmt(true);
    } else {
      setExceedsContribAmt(false);
    }
    setTotalInvestmentAmt(priorYearTotal);
  };

  return (
    <div className="review-customer-information">
      <div className="section-header">
        <SectionHeading text="My Information" />
      </div>

      {editCustomer ? (
        <>
          <FONASUCustomerInformationSection
            phone1={phone1}
            phone2={phone2}
            phone3={phone3}
            phoneNumberRequired={phoneNumberRequired}
            setPhoneNumberRequired={setPhoneNumberRequired}
            mailingAddressDifferent={mailingAddressDifferent}
          />
          <FONASUContriInvestmentAmount
            formProps={formProps}
            handleCurrentYearAmt={handleCurrentYearAmt}
            handlePriorYearAmt={handlePriorYearAmt}
            totalInvestmentAmt={totalInvestmentAmt}
            priorYearInvAmount={priorYearInvAmount}
            curYearInvAmount={curYearInvAmount}
            contributionLimits={contributionLimits}
            meetsMinInvestmentAmt={meetsMinInvestmentAmt}
            exceedsContribAmt={exceedsContribAmt}
          />
        </>
      ) : (
        <CustomerContribReview
          customer={customer}
          phone1={phone1}
          phone2={phone2}
          phone3={phone3}
          formProps={formProps}
          currentYearAmount={currentYearAmount}
          priorYearAmount={priorYearAmount}
          totalInvestmentAmt={totalInvestmentAmt}
          contributionLimits={contributionLimits}
        />
      )}
      <EditButton
        onClick={handleClick}
        currentlyEditing={editCustomer}
        disabled={disableDoneBtn}
      />
      <style jsx>{styles}</style>
    </div>
  );
};

export default FONASUContribCustomerInfo;
